import React, { useState } from "react";
import styled, { css } from "styled-components";
import PropTypes from "prop-types";

import RadioInput from "core/RadioInput";
import { rapidEasing } from "core/animation";
import SrOnly from "core/SrOnly";

import breakpoints from "core/breakpoints";
import colors from "core/colors";

import ButtonContent from "./ButtonContent";

const Fieldset = styled.fieldset`
    display: flex;
    gap: 1.25rem;

    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        gap: 1rem;
    }
`;

export const RadioButtonLabel = styled.label`
    flex-grow: ${({ grow }) => (grow ? "1" : "0")};
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    padding: 1.6875rem 1.5rem;
    background: ${colors.primary.snow};
    color: ${colors.secondary.orca};
    border-radius: 4px;
    transition: all ${rapidEasing};
    box-shadow: inset 0 0 0 2px ${colors.primary.scheifer};

    ${({ selected }) =>
        selected &&
        css`
            background-color: ${colors.accent.lightMode.dewdrop};
            box-shadow: inset 0 0 0 4px ${colors.accent.lightMode.pansy};
        `}

    /* when radio input has keyboard focus */
    &:has(:focus-visible) {
        background-color: ${colors.primary.snow};
        box-shadow: inset 0 0 0 2px ${colors.secondary.orca};
    }

    &:hover {
        ${({ selected }) =>
            !selected &&
            css`
                background-color: ${colors.accent.lightMode.dewdrop};
                box-shadow: inset 0 0 0 0 transparent;
            `}
    }

    ${RadioInput}:focus {
        outline: none;
        box-shadow: none;
    }

    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        justify-content: center;

        /* visually hide radio input on mobile */
        ${RadioInput} {
            clip: rect(0 0 0 0);
            clip-path: inset(50%);
            height: 1px;
            overflow: hidden;
            position: absolute;
            white-space: nowrap;
            width: 1px;
        }
    }
`;

let RadioButtons = ({
    className,
    radioName = "",
    onClick = () => {},
    defaultCheckedValue,
    fieldsetTitle,
    options = [],
    expandToContainerWidth
}) => {
    const [selected, setSelected] = useState(
        defaultCheckedValue || options[0].value
    );

    const handleChange = e => {
        setSelected(e.target.value);
        onClick(e.target.value);
    };

    return (
        <>
            <SrOnly as="legend">{fieldsetTitle}</SrOnly>
            <Fieldset
                role="radiogroup"
                className={className}
                onChange={handleChange}
            >
                {options.map(option => {
                    const optionSelected = selected === option.value;

                    return (
                        <RadioButtonLabel
                            key={option.value}
                            selected={optionSelected}
                            grow={expandToContainerWidth}
                        >
                            <RadioInput
                                name={radioName}
                                value={option.value}
                                checked={optionSelected}
                                onChange={() => {}}
                                // intentional empty onChange function
                                // suppress react warning about onChange required
                                // we are handling this on the Fieldset onChange above
                            />
                            <ButtonContent option={option} />
                        </RadioButtonLabel>
                    );
                })}
            </Fieldset>
        </>
    );
};

RadioButtons = styled(RadioButtons)``;

RadioButtons.propTypes = {
    className: PropTypes.string,
    radioName: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    defaultCheckedValue: PropTypes.string,
    fieldsetTitle: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.object).isRequired,
    expandToContainerWidth: PropTypes.bool
};

export default RadioButtons;
